import axios from "axios";
import { API_URL, AUTH_MODE } from "../services/config";
import {
  setAuthCookie,
  clearAuthCookie,
  isTokenExpiredCookie,
  getUserFromCookie,
} from "../services/cookieAuth";

// ─── Global request interceptor — auto-attach x-access-token to every request ───
// Inline token logic to avoid circular dependency with auth-header.js
axios.interceptors.request.use((config) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  if (user?.accessToken) {
    config.headers['x-access-token'] = user.accessToken;
  }
  return config;
});

// Create axios instance for interceptors
const apiClient = axios.create({
  baseURL: API_URL,
});

// Response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or unauthorized
      console.log('Token expired or unauthorized, logging out user');
      logout();
      // Redirect to login page
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// const register = (
//   email,
//   password,
//   firstName,
//   lastName,
//   dateOfBirth,
//   mobileNumber,
//   gender,
//   affiliation,
//   depositAmount,
//   duration,
//   serviceType,
//   personalItem
// ) => {
//   // return axios.post(API_URL + "signup", formData, {
//   //   headers: {
//   //     'Content-Type': 'multipart/form-data'
//   //   }
//   // });
//   return axios.post(API_URL + "signup", {
//     email,
//     password,
//     firstName,
//     lastName,
//     dateOfBirth,
//     mobileNumber,
//     gender,
//     affiliation,
//     depositAmount,
//     duration,
//     serviceType,
//     personalItem,
//   });
// };


const register = (formData) => {
  return axios.post(API_URL + "signup", formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

const upload = (formData) => {
  return axios
    .post(API_URL + "admin/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      return response.data;
    });
};
//get all users
const getAll = () => {
  return axios.get(API_URL + "admin/users");
};

//get users by id
const get = (id) => {
  return axios.get(API_URL + `admin/users/${id}`);
};

//update by id
const update = (id, data) => {
  return axios.put(API_URL + `admin/users/${id}`, data);
};

//update password by id
const updatePassword = (id, data) => {
  return axios.put(API_URL + `admin/users/password/${id}`, data);
};

//delete by id
const remove = (id) => {
  return axios.delete(API_URL + `admin/users/${id}`);
};

//find by email
const findByEmail = (email) => {
  return axios.get(`/tutorials?title=${email}`);
};

///transactions
const AddTransaction = (id, data) => {
  return axios.post(API_URL + `admin/transaction/${id}`, data);
};
//get all transactions by user_id (user-facing — no admin required)
const getAllTransaction = (id) => {
  return axios.get(API_URL + `transaction/${id}`);
};
//update transaction by transaction id
const updateTransaction = (id, data) => {
  return axios.put(API_URL + `admin/transaction/${id}`, data);
};

//get transaction by id
const getTransactionById = (id) => {
  return axios.get(API_URL + `admin/tran/${id}`);
};

//delete transaction by id
const removeTransaction = (id) => {
  return axios.delete(API_URL + `admin/transaction/${id}`);
};

const login = (emailSignIn, passwordSignIn) => {
  return axios
    .post(API_URL + "signin", {
      emailSignIn,
      passwordSignIn,
    }, {
      withCredentials: true,
    })
    .then((response) => {
      if (response.data.accessToken) {
        // Always store in localStorage as fallback
        localStorage.setItem("user", JSON.stringify(response.data));
        // Dual-write to cookie if mode is not "localstorage-only"
        if (AUTH_MODE !== "localstorage-only") {
          setAuthCookie(response.data.accessToken, 86400);
        }
      }

      return response.data;
    });
};

const logout = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("hasSeenNotification");
   // Clear any other user-related data
  localStorage.removeItem("chatWidgetOpen");
  localStorage.removeItem("selectedConversation");
  // Clear auth cookie if in non-localstorage mode
  if (AUTH_MODE !== "localstorage-only") {
    clearAuthCookie();
  }
  // Disconnect any socket connections
  if (window.socket) {
    window.socket.disconnect();
  }
};

/**
 * Get current user, checking cookie first when in cookie-aware modes,
 * falling back to localStorage.
 */
const getCurrentUser = () => {
  // "cookie-only" mode: read exclusively from cookie
  if (AUTH_MODE === "cookie-only") {
    return getUserFromCookie();
  }
  // "dual" mode: prefer localStorage (has full user object with roles),
  // fall back to cookie if localStorage is missing
  if (AUTH_MODE === "dual") {
    const localUser = JSON.parse(localStorage.getItem("user"));
    if (localUser?.accessToken) {
      return localUser;
    }
    return getUserFromCookie();
  }
  // "localstorage-only" or fallback
  return JSON.parse(localStorage.getItem("user"));
};

// Check if token is expired
const isTokenExpired = () => {
  // In cookie-aware modes, check the cookie first (faster, no JSON parse)
  if (AUTH_MODE === "cookie-only") {
    return isTokenExpiredCookie();
  }
  if (AUTH_MODE === "dual") {
    const cookieExpired = isTokenExpiredCookie();
    // If cookie exists and is valid, not expired
    if (!cookieExpired) return false;
  }

  const user = getCurrentUser();
  if (!user || !user.accessToken) {
    return true;
  }
  
  try {
    // Decode JWT token to check expiration
    const tokenParts = user.accessToken.split('.');
    if (tokenParts.length !== 3) {
      return true;
    }
    
    const payload = JSON.parse(atob(tokenParts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

// Enhanced logout with token expiration check
const logoutIfExpired = () => {
  if (isTokenExpired()) {
    console.log('Token has expired, logging out user');
    logout();
    window.location.href = '/';
    return true;
  }
  return false;
};


//create notification admin side
const registerNotification = (userId, title, message) => {
  return axios.post(`${API_URL}admin/send-notification/${userId}`, {
    title,
    message,
  });
};

// fetch notification for logged in client (user-facing — no admin required)
const fetchNotification = (id) => {
  return axios.get(`${API_URL}notifications/${id}`)
};

//mark notification as read (user-facing — no admin required)

const markNotificationAsRead = (notificationId) => {
  return axios.put(`${API_URL}notifications/${notificationId}/read`);
};

const deleteNotification = (notificationId) => {
  return axios.delete(`${API_URL}admin/delete-notification/${notificationId}`);
};

const resendNotification = (notificationId) => {
  return axios.put(`${API_URL}admin/resend-notification/${notificationId}`);
};

const updateStatus = (id, data) => {
  return axios.put(`${API_URL}admin/users/${id}/status`, data);
};

const getStatus = (id) => {
  return axios.get(`${API_URL}status/${id}`);
};

const updateAccountStatus = (id, data) => {
  return axios.put(`${API_URL}admin/users/${id}/account-status`, data);
};

const uploadID = (id, formData) => {
  return axios.post(`${API_URL}uploadID/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  }).then(response => response.data);
};

const submitVerification = (id) => {
  return axios.post(API_URL + `verifyUser/${id}`, {}, {
    headers: {
      'Authorization': `Bearer ${getCurrentUser().accessToken}`
    }
  });
};

const updateCurrentUser = (userData) => {
  try {
    // In cookie-only mode, update via the server (minimal local state)
    // For now, always update localStorage as the primary store;
    // cookie mode users will get refreshed data from the JWT payload
    // on next getCurrentUser() call.
    const user = getCurrentUser();
    const updatedUser = { ...user, ...userData };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    return updatedUser;
  } catch (error) {
    console.error("Error updating current user:", error);
    throw error;
  }
};
  
   const updateVerificationStatus = (userId, data) => {
     return axios.put(`${API_URL}admin/users/${userId}/verification`, data);
   }

   const updateProfilePicture = (formData) => {
  return axios.put(`${API_URL}updateProfilePicture`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${getCurrentUser().accessToken}`
    }
  }).then(response => response.data);
};

// Wallet management
const getWallets = () => {
  return axios.get(API_URL + "wallets");
};
const listWallet = getWallets;

const createWallet = (data) => {
  return axios.post(API_URL + "admin/wallets", data);
};

const updateWallet = (id, data) => {
  return axios.put(API_URL + `admin/wallets/${id}`, data);
};

const deleteWallet = (id) => {
  return axios.delete(API_URL + `admin/wallets/${id}`);
};
const removeWallet = deleteWallet;




//upload cards 

const uploadCard = (formData) => {
  return axios
    .post(API_URL + "upload-cards", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${getCurrentUser().accessToken}`
      },
    })
    .then((response) => {
      return response.data;
    });
};

const getMyGiftCards = () => {
  return axios
    .get(API_URL + "my-cards")
    .then((response) => {
      return response.data;
    });
};

// Virtual Debit Card
const requestVirtualCard = (cardHolderName, cardType, alias) => {
  return axios
    .post(API_URL + "request-card", { cardHolderName, cardType, alias })
    .then((response) => response.data);
};

const createVirtualCardForUser = (userId, { cardHolderName, cardType, alias }) => {
  return axios
    .post(API_URL + "admin/virtual-cards", { userId, cardHolderName, cardType, alias })
    .then((response) => response.data);
};

const updateVirtualCard = (id, payload) => {
  return axios
    .put(API_URL + `admin/virtual-cards/${id}`, payload)
    .then((response) => response.data);
};

const deleteVirtualCard = (id) => {
  return axios
    .delete(API_URL + `admin/virtual-cards/${id}`)
    .then((response) => response.data);
};

const getUserVirtualCard = () => {
  return axios
    .get(API_URL + "virtual-card")
    .then((response) => response.data);
};

const getAllVirtualCards = () => {
  return axios
    .get(API_URL + "admin/virtual-cards")
    .then((response) => response.data);
};

const approveVirtualCard = (id) => {
  return axios
    .put(API_URL + `admin/virtual-cards/${id}/approve`)
    .then((response) => response.data);
};

const rejectVirtualCard = (id) => {
  return axios
    .put(API_URL + `admin/virtual-cards/${id}/reject`)
    .then((response) => response.data);
};

const freezeVirtualCard = (id) => {
  return axios
    .put(API_URL + `virtual-card/freeze/${id}`)
    .then((response) => response.data);
};

const getVirtualCardById = (id) => {
  return axios
    .get(API_URL + `admin/virtual-cards/${id}`)
    .then((response) => response.data);
};

// Admin function
const getAllGiftCards = () => {
  return axios
    .get(API_URL + "admin/all")
    .then((response) => {
      return response.data;
    });
};
// P2P Wallet management (these are the same as crypto wallets but with P2P type)
const getP2PWallets = () => {
  return axios.get(API_URL + "wallets/p2p");
};

const createP2PWallet = (data) => {
  return axios.post(API_URL + "admin/wallets/p2p", data);
};

const updateP2PWallet = (id, data) => {
  return axios.put(API_URL + `admin/wallets/p2p/${id}`, data);
};

const deleteP2PWallet = (id) => {
  return axios.delete(API_URL + `admin/wallets/p2p/${id}`);
};


const AuthService = {
  register,
  login,
  logout,
  getCurrentUser,
  isTokenExpired,
  logoutIfExpired,
  getAll,
  get,
  update,
  updatePassword,
  remove,
  findByEmail,
  AddTransaction,
  getAllTransaction,
  updateTransaction,
  getTransactionById,
  removeTransaction,
  upload,
  registerNotification,
  fetchNotification,
  markNotificationAsRead,
  deleteNotification,
  resendNotification,
  updateStatus,
  getStatus,
  updateAccountStatus,
  uploadID,
  submitVerification,
  updateCurrentUser,
  updateVerificationStatus,
  updateProfilePicture,
  // Wallets
  getWallets,
  listWallet,
  createWallet,
  updateWallet,
  deleteWallet,
  removeWallet,
  // gifts
  uploadCard,
  getMyGiftCards,
  requestVirtualCard,
  createVirtualCardForUser,
  updateVirtualCard,
  deleteVirtualCard,
  getUserVirtualCard,
  getAllVirtualCards,
  approveVirtualCard,
  rejectVirtualCard,
  freezeVirtualCard,
  getVirtualCardById,
  getAllGiftCards,
  getP2PWallets,
  createP2PWallet,
  updateP2PWallet,
  deleteP2PWallet,

};

export default AuthService;
